import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import SVGIcon from '../components/SVGIcon';
import { PostType } from '../graphql/generated/graphql';
import { getPostIcon, getPostState } from '../post/PostIcon';
import PostTitleWithIcons from '../post/PostTitleWithIcons';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import sortTopic from '../topic/utils/sortTopic';
import postPath from '../utils/url/postPath';
import styles from './SidebarItems.module.scss';
import SidebarNavItem from './SidebarNavItem';
import sortSidebarItems from './sortSidebarItems';
import TopicItem from './TopicItem';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const POST_FRAGMENT = gql(`
  fragment SidebarItems_post on Post {
    id
    type
    ...PostTitleWithIcons_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

interface SidebarItemsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
  showIcon?: boolean;
  posts?: PostFragment[];
  topics: AllTopicsTreeListTopic[];
  orderIds?: string[];
  className?: string;
}

type RenderItem =
  | (AllTopicsTreeListTopic & { sidebarType: 'topic' })
  | (PostFragment & { sidebarType: 'post' });

const SidebarItems = ({
  activeTopics,
  expandedTopics,
  showIcon,
  posts = [],
  topics,
  orderIds,
  className,
}: SidebarItemsProps) => {
  const [forcedExpandedKeys, setForceExpandedKeys] = useState<
    Record<string, boolean>
  >({});
  const renderItems: React.ReactNode[] = [];

  const items: RenderItem[] = useMemo(() => {
    const combined: RenderItem[] = [
      ...topics.map((topic) => ({ ...topic, sidebarType: 'topic' as const })),
      ...posts.map((post) => ({ ...post, sidebarType: 'post' as const })),
    ];
    return Array.isArray(orderIds)
      ? sortSidebarItems(combined, orderIds)
      : combined;
  }, [topics, posts, orderIds]);

  const renderTopic = (
    topic: AllTopicsTreeListTopic,
    path: string[],
    isFirstChild?: boolean,
  ) => {
    const { childTopics, id } = topic;
    const key = [...path, id].join('.');
    const isForceExpanded = forcedExpandedKeys[key];
    const isExpanded =
      isForceExpanded !== undefined
        ? isForceExpanded
        : expandedTopics[id] || activeTopics[id];

    renderItems.push(
      <TopicItem
        key={key}
        topic={topic}
        active={activeTopics[id]}
        expanded={isExpanded}
        level={path.length}
        first={isFirstChild}
        showIcon={path.length === 0 && showIcon}
        alignToIcon={path.length > 0 && showIcon}
        onToggleExpand={() =>
          setForceExpandedKeys({ ...forcedExpandedKeys, [key]: !isExpanded })
        }
      />,
    );

    if (isExpanded && childTopics.length > 0) {
      const childPath = [...path, id];
      childTopics
        .sort(sortTopic)
        .forEach((t, i) => renderTopic(t, childPath, i === 0));
    }
  };

  const renderPost = (post: PostFragment) => {
    const postState = getPostState(post);
    renderItems.push(
      <li key={post.id}>
        <SidebarNavItem
          archived={postState.archived}
          icon={
            <SVGIcon className={styles.icon} size={12}>
              {getPostIcon(post, !postState.draft)}
            </SVGIcon>
          }
          to={postPath(post)}
          subItem
          type={post.type === PostType.Template ? 'template' : 'post'}
        >
          <div className={styles.postTitle}>
            <PostTitleWithIcons
              post={post}
              truncate="multi"
              iconSize="small"
              showVerifiedIcon={false}
              publicIconMuted
            />
          </div>
        </SidebarNavItem>
      </li>,
    );
  };

  items.forEach((item) => {
    if (item.sidebarType === 'post') {
      renderPost(item);
    } else if (item.sidebarType === 'topic') {
      renderTopic(item, []);
    }
  });

  return <ul className={classNames(styles.list, className)}>{renderItems}</ul>;
};

export default SidebarItems;
